import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ShoppingBag, ArrowLeft } from "@phosphor-icons/react";
import { api, startCheckout, errMsg } from "@/lib/api";
import { PageHead } from "@/components/Layout";
import { useT } from "@/i18n";

export function TemplatesPage() {
  const { t: tr, lang } = useT();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/templates").then((r) => setItems(r.data)); }, [lang]);
  return (
    <div data-testid="templates-page">
      <PageHead eyebrow="Templates shop" title="Buy once. Use for every guest." sub="Printable PDF and Word templates written by hosts, checked by hand. Download links right after checkout. Prices include VAT." />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid md:grid-cols-3 gap-8">
        {items.map((t, i) => (
          <motion.div key={t.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <Link to={`/templates/${t.id}`} className="card card-hover block overflow-hidden h-full" data-testid={`template-card-${t.id}`}>
              <img src={`${t.image}?auto=compress&w=700`} alt={t.name} className="w-full h-52 object-cover" />
              <div className="p-6">
                <div className="flex justify-between items-start gap-4">
                  <h3 className="text-xl text-[#0F4C5C]">{t.name}</h3>
                  <span className="font-display text-2xl text-[#E36414]">€{t.price}</span>
                </div>
                <p className="mt-2 text-gray-700 text-sm">{t.tagline}</p>
                <p className="mt-4 text-xs text-gray-500">{t.format}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="card p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center !bg-[#0F4C5C] !border-[#0F4C5C] text-[#F4E9D8]" data-testid="makeover-offer">
          <div>
            <p className="text-xs uppercase tracking-widest opacity-70">{tr("Done for you")}</p>
            <h3 className="text-2xl md:text-3xl mt-1">{tr("Listing makeover by a real host-copywriter")}</h3>
            <p className="mt-2 opacity-80 max-w-xl">{tr("Send us your listing, get back a full rewrite in 2 languages within 3 working days. Limited launch slots.")}</p>
          </div>
          <BuyButton lookupKey="svc_makeover" label={tr("Book for €79")} testid="makeover-buy-btn" />
        </div>
      </section>
    </div>
  );
}

const BuyButton = ({ lookupKey, label, testid }) => {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try { await startCheckout(lookupKey); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return <button onClick={buy} disabled={busy} className="btn-coral justify-center" data-testid={testid}><ShoppingBag size={18} weight="fill" /> {busy ? t("Opening checkout…") : label}</button>;
};

export function TemplateDetail() {
  const { t: tr, lang } = useT();
  const { id } = useParams();
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/templates/${id}`).then((r) => setT(r.data)).catch(() => setT(false)); }, [id, lang]);
  if (t === false) return <p className="p-16">{tr("Template not found.")}</p>;
  if (!t) return <div className="p-16"><div className="skeleton h-80 max-w-4xl" /></div>;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="template-detail-page">
      <Link to="/templates" className="text-sm text-[#0F4C5C] inline-flex items-center gap-1 hover:text-[#E36414]" data-testid="template-back-link"><ArrowLeft size={14} /> {tr("All templates")}</Link>
      <div className="mt-6 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
        <img src={`${t.image}?auto=compress&w=1100`} alt={t.name} className="rounded-2xl w-full h-[420px] object-cover border border-[#E2DCD0]" />
        <div>
          <h1 className="text-4xl sm:text-5xl text-[#0F4C5C]" data-testid="template-detail-name">{t.name}</h1>
          <p className="mt-4 text-lg text-gray-700">{t.tagline}</p>
          <p className="mt-6 font-display text-5xl text-[#E36414]" data-testid="template-detail-price">€{t.price}</p>
          <p className="text-xs text-gray-500 mt-1">{tr("One-time payment · VAT included")} · {t.format}</p>
          <ul className="mt-8 space-y-3">{t.includes.map((x) => <li key={x} className="flex gap-2"><Check size={20} weight="bold" className="text-[#0F4C5C] shrink-0" /> {x}</li>)}</ul>
          <div className="mt-10"><BuyButton lookupKey={t.lookup_key} label={tr("Buy for €{price}", { price: t.price })} testid="template-buy-btn" /></div>
        </div>
      </div>
    </div>
  );
}
